import { AfterContentChecked, Directive, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription, timer } from 'rxjs';

enum ScrollbarState {
  Active,
  Hover,
  Visible,
  Fading,
  Hidden
}

enum ScrollMode {
  Auto,
  Hover,
  Visible,
  Hidden
}

@Directive({
  selector: '[scrollable]'
})
export class ScrollableDirective implements OnInit, OnDestroy, AfterContentChecked {
  static WIDTH: number = 7;
  static MARGIN: number = 2;
  static NORMAL_OPACITY: number = 0.5;
  static HOVER_OPACITY: number = 0.55;
  static ACTIVE_OPACTIY: number = 0.6;
  static MS_UNTIL_FADE_OUT: number = 750;
  static FADE_OUT_MS: number = 400;

  @Input() set scrollable(scrollable: ScrollableConfig) {
    [0,1].forEach((sIndex: number) => {
      switch(sIndex == 0 ? scrollable.x : scrollable.y) {
        case 'auto':
          this.scrollModes[sIndex] = ScrollMode.Auto;
          break;
        case 'hover':
          this.scrollModes[sIndex] = ScrollMode.Hover;
          break;
        case 'visible':
          this.scrollModes[sIndex] = ScrollMode.Visible;
          break;
        default:
          this.scrollModes[sIndex] = ScrollMode.Hidden;
      }
      if(!this.isMouseState(this.scrollbarStates[sIndex]))
        this.initMode(sIndex);
    });
  }

  scrollModes: ScrollMode[];
  animationFrames: number[];
  scrollbars: any[];
  timerSubscriptions: Subscription[];
  scrollbarStates: ScrollbarState[];
  lastAnimatedScrollbarStates: ScrollbarState[];
  lastScrolls: number[];
  hostHovered: boolean;
  hovering: boolean[];
  dragging: number;
  dragPoint: number;
  pointerPos: number[];
  pointerId: number;

  constructor(private ngZone: NgZone, private element: ElementRef) {
    this.scrollModes = [ScrollMode.Hidden, ScrollMode.Hidden];
    this.animationFrames = [null, null];
    this.scrollbars = [null, null];
    this.timerSubscriptions = [null, null];
    this.scrollbarStates = [ScrollbarState.Hidden, ScrollbarState.Hidden];
    this.lastAnimatedScrollbarStates = [null, null];
    this.lastScrolls = [0, 0];
    this.hostHovered = false;
    this.hovering = [false, false];
    this.dragging = null;
    this.pointerPos  = [0, 0];
    this.pointerId = null;
  }

  ngOnInit() {
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('mousemove', this.handleWindowMouseMove);
      window.addEventListener('mouseup', this.handleWindowMouseUp);
      window.addEventListener('touchmove', this.handleWindowTouchMove);
      window.addEventListener('touchend', this.handleWindowTouchCancelEnd);
      window.addEventListener('touchcancel', this.handleWindowTouchCancelEnd);
    });
    if(getComputedStyle(this.element.nativeElement).position == 'static')
      this.element.nativeElement.style.position = 'relative';
  }

  ngOnDestroy() {
    this.ngZone.runOutsideAngular(() => {
      window.removeEventListener('mousemove', this.handleWindowMouseMove);
      window.removeEventListener('mouseup', this.handleWindowMouseUp);
      window.removeEventListener('touchmove', this.handleWindowTouchMove);
      window.removeEventListener('touchend', this.handleWindowTouchCancelEnd);
      window.removeEventListener('touchcancel', this.handleWindowTouchCancelEnd);
    });
    [0,1].forEach((sIndex: number) => {
      this.cancelAutoScrollingTimer(sIndex);
      const animationFrame: number = this.animationFrames[sIndex];
      if(animationFrame != null)
        this.ngZone.runOutsideAngular(() => cancelAnimationFrame(animationFrame));
      const scrollbar: any = this.scrollbars[sIndex];
      if(scrollbar)
        scrollbar.remove();
    });
  }

  ngAfterContentChecked() {
    this.updateScrollbar(0);
    this.updateScrollbar(1);
  }

  initMode(sIndex: number) {
    const scrollbarState: ScrollbarState = this.scrollbarStates[sIndex];
    switch(this.scrollModes[sIndex]) {
      case ScrollMode.Auto:
        if(scrollbarState == ScrollbarState.Visible || this.isMouseState(scrollbarState))
          this.updateAutoScrolling(sIndex);
        break;
      case ScrollMode.Hover:
        this.cancelAutoScrollingTimer(sIndex);
        this.updateHoverScrolling(sIndex);
        break;
      case ScrollMode.Visible:
        this.cancelAutoScrollingTimer(sIndex);
        if(scrollbarState != ScrollbarState.Visible) {
          this.scrollbarStates[sIndex] = ScrollbarState.Visible;
          this.updateScrollbar(sIndex);
        }
        break;
      default:
        if(scrollbarState != ScrollbarState.Hidden) {
          this.cancelAutoScrollingTimer(sIndex);
          this.scrollbarStates[sIndex] = ScrollbarState.Hidden;
          this.updateScrollbar(sIndex);
        }
    }
  }

  isMouseState(state: ScrollbarState): boolean {
    return state == ScrollbarState.Hover || state == ScrollbarState.Active;
  }

  setOpacityInstant(scrollbar: any, opacity: number) {
    scrollbar.style.transitionDuration = '0ms';
    scrollbar.style.opacity = opacity.toString();
    scrollbar.offsetHeight;
    scrollbar.style.transitionDuration = ScrollableDirective.FADE_OUT_MS + 'ms';
  }

  updateScrollbar(sIndex: number) {
    if(this.animationFrames[sIndex] != null)
      return;
    this.ngZone.runOutsideAngular(() => {
      this.animationFrames[sIndex] = requestAnimationFrame(() => {
        let scrollbar: any = this.scrollbars[sIndex];
        const scrollbarState: ScrollbarState = this.scrollbarStates[sIndex];
        if(scrollbarState != ScrollbarState.Hidden && (sIndex == 0 ? (this.element.nativeElement.scrollWidth > this.element.nativeElement.offsetWidth) : (this.element.nativeElement.scrollHeight > this.element.nativeElement.offsetHeight))) {
          if(!scrollbar) {
            if(scrollbarState == ScrollbarState.Fading) {
              this.updateAutoScrolling(sIndex);
              return;
            }
            scrollbar = document.createElement('div');
            scrollbar.style = 'position:absolute;width:' + (sIndex == 0 ? 0 : ScrollableDirective.WIDTH) + 'px;height:'
              + (sIndex == 0 ? ScrollableDirective.WIDTH : 0) + 'px;background-color:black;opacity:'
              + ScrollableDirective.NORMAL_OPACITY + ';transition:opacity ' + ScrollableDirective.FADE_OUT_MS
              + 'ms;border-radius:' + (ScrollableDirective.WIDTH / 2) + 'px;'
              + (sIndex == 0 ? 'bottom:' : 'top:') + ScrollableDirective.MARGIN + 'px;'
              + (sIndex == 0 ? 'left:' : 'right:') + ScrollableDirective.MARGIN + 'px;touch-action:none;z-index:1;'
            scrollbar.addEventListener('mouseenter', () => this.onMouseEnterScrollbar(sIndex));
            scrollbar.addEventListener('mouseleave', () => this.onMouseLeaveScrollbar(sIndex));
            scrollbar.addEventListener('mousedown', (event: MouseEvent) => this.onMouseDownScrollbar(sIndex, sIndex == 0 ? event.clientX : event.clientY));
            scrollbar.addEventListener('touchstart', (event: TouchEvent) => this.onTouchStartScrollbar(sIndex, event));
            scrollbar = this.element.nativeElement.appendChild(scrollbar);
            this.scrollbars[sIndex] = scrollbar;
            if(!scrollbar)
              return;
          }
          if(scrollbarState != this.lastAnimatedScrollbarStates[sIndex]) {
            switch(scrollbarState) {
              case ScrollbarState.Visible:
                this.setOpacityInstant(scrollbar, ScrollableDirective.NORMAL_OPACITY);
                break;
              case ScrollbarState.Fading:
                scrollbar.style.opacity = '0';
                break;
              case ScrollbarState.Active:
                this.setOpacityInstant(scrollbar, ScrollableDirective.ACTIVE_OPACTIY);
                break;
              case ScrollbarState.Hover:
                this.setOpacityInstant(scrollbar, ScrollableDirective.HOVER_OPACITY);
                break;
              default:
            }
          }
          const domRect: DOMRect = this.element.nativeElement.getBoundingClientRect();
          let translateX: number = 0;
          let translateY: number = 0;
          if(sIndex == 0) {
            const scrollLeft: number = this.element.nativeElement.scrollLeft;
            const scrollWidth: number = this.element.nativeElement.scrollWidth;
            const space: number = domRect.width - ScrollableDirective.MARGIN * 2;
            const width: number = space * this.element.nativeElement.offsetWidth / scrollWidth;
            translateX = scrollLeft + Math.max(0, Math.min(space - width, space * scrollLeft / scrollWidth))
            translateY = this.element.nativeElement.scrollTop;
            scrollbar.style.width = width + 'px';
          } else {
            const scrollTop: number = this.element.nativeElement.scrollTop;
            const scrollHeight: number = this.element.nativeElement.scrollHeight;
            const space: number = domRect.height - ScrollableDirective.MARGIN * 2;
            const height: number = space * this.element.nativeElement.offsetHeight / scrollHeight;
            translateX = this.element.nativeElement.scrollLeft;
            translateY = scrollTop + Math.max(0, Math.min(space - height, space * scrollTop / scrollHeight));
            scrollbar.style.height = height + 'px';
          }
          scrollbar.style.transform = 'translate(' + translateX + 'px,' + translateY + 'px)';
        } else if(scrollbar) {
          scrollbar.remove();
          this.scrollbars[sIndex] = null;
        }
        this.lastAnimatedScrollbarStates[sIndex] = scrollbarState;
        this.animationFrames[sIndex] = null;
      });
    });
    if(this.scrollbarStates[sIndex] == ScrollbarState.Active) {
      const domRect: DOMRect = this.element.nativeElement.getBoundingClientRect();
      if(sIndex == 0) {
        this.element.nativeElement.scrollLeft = (this.pointerPos[sIndex] - this.dragPoint - domRect.left - ScrollableDirective.MARGIN)
          / ((domRect.width - ScrollableDirective.MARGIN * 2) / this.element.nativeElement.scrollWidth);
      } else {
        this.element.nativeElement.scrollTop = (this.pointerPos[sIndex] - this.dragPoint - domRect.top - ScrollableDirective.MARGIN)
          / ((domRect.height - ScrollableDirective.MARGIN * 2) / this.element.nativeElement.scrollHeight);
      }
    }
  }

  cancelAutoScrollingTimer(sIndex: number) {
    if(this.timerSubscriptions[sIndex])
      this.timerSubscriptions[sIndex].unsubscribe();
  }

  updateAutoScrolling(sIndex: number) {
    this.cancelAutoScrollingTimer(sIndex)
    this.scrollbarStates[sIndex] = ScrollbarState.Visible;
    this.timerSubscriptions[sIndex] = timer(ScrollableDirective.MS_UNTIL_FADE_OUT).subscribe(() => {
      this.scrollbarStates[sIndex] = ScrollbarState.Fading;
      this.timerSubscriptions[sIndex] = timer(ScrollableDirective.FADE_OUT_MS).subscribe(() => {
        this.timerSubscriptions[sIndex] = null;
        this.scrollbarStates[sIndex] = ScrollbarState.Hidden;
        this.updateScrollbar(sIndex);
      });
      this.updateScrollbar(sIndex);
    });
    this.updateScrollbar(sIndex);
  }

  updateHoverScrolling(sIndex: number) {
    const newState: ScrollbarState = this.hostHovered ? ScrollbarState.Visible : ScrollbarState.Hidden;
    if(newState != this.scrollbarStates[sIndex]) {
      this.scrollbarStates[sIndex] = newState;
      this.updateScrollbar(sIndex);
    }
  }

  findTouch(touchList: TouchList, condition: (touch: Touch) => boolean): Touch {
    let i: number = 0;
    for(let i = 0; i < touchList.length; i++) {
      const touch: Touch = touchList.item(i);
      if(condition(touch))
        return touch;
    }
    return null;
  }

  onPointerDown(sIndex: number, position: number) {
    this.pointerPos[sIndex] = position;
    this.dragging = sIndex;
    this.dragPoint = position - this.scrollbars[sIndex].getBoundingClientRect()[sIndex == 0 ? 'left' : 'top'];
    this.scrollbarStates[sIndex] = ScrollbarState.Active;
    this.updateScrollbar(sIndex);
  }

  onPointerMove(clientX: number, clientY: number) {
    const oldPos: number = this.dragging != null ? this.pointerPos[this.dragging] : null;
    this.pointerPos = [clientX, clientY];
    if(this.dragging != null && this.pointerPos[this.dragging] != oldPos)
      this.updateScrollbar(this.dragging);
  }

  onPointerUp() {
    const dragging: number = this.dragging;
    if(dragging != null) {
      this.dragging = null;
      this.dragPoint = null;
      if(this.hovering[dragging]) {
        this.scrollbarStates[dragging] = ScrollbarState.Hover;
        this.updateScrollbar(dragging);
      } else
        this.initMode(dragging);
    }
  }

  onMouseEnterScrollbar(sIndex: number) {
    this.hovering[sIndex] = true;
    if(this.scrollbarStates[sIndex] != ScrollbarState.Active) {
      this.cancelAutoScrollingTimer(sIndex);
      this.scrollbarStates[sIndex] = ScrollbarState.Hover;
      this.updateScrollbar(sIndex);
    }
  }

  onMouseLeaveScrollbar(sIndex: number) {
    this.hovering[sIndex] = false;
    if(this.scrollbarStates[sIndex] != ScrollbarState.Active)
      this.initMode(sIndex);
  }

  onMouseDownScrollbar(sIndex: number, position: number) {
    if(this.scrollbarStates[sIndex] != ScrollbarState.Active)
      this.onPointerDown(sIndex, position);
  }

  onTouchStartScrollbar(sIndex: number, event: TouchEvent) {
    if(this.scrollbarStates[sIndex] == ScrollbarState.Active)
      return;
    const scrollbar: any = this.scrollbars[sIndex];
    const touch: Touch = this.findTouch(event.changedTouches, (touch: Touch): boolean => touch.target == scrollbar);
    if(touch) {
      event.preventDefault();
      this.pointerId = touch.identifier;
      this.onPointerDown(sIndex, sIndex == 0 ? touch.clientX : touch.clientY);
    }
  }

  @HostListener('scroll')
  onScroll() {
    const scrolls: number[] = [this.element.nativeElement.scrollLeft, this.element.nativeElement.scrollTop];
    [0,1].forEach((sIndex: number) => {
      let updated: boolean = false;
      if(scrolls[sIndex] != this.lastScrolls[sIndex]) {
        if(this.scrollModes[sIndex] == ScrollMode.Auto && !this.isMouseState(this.scrollbarStates[sIndex])) {
          this.updateAutoScrolling(sIndex);
          updated = true;
        }
        this.lastScrolls[sIndex] = scrolls[sIndex];
      }
      if(!updated)
        this.updateScrollbar(sIndex);
    });
  }

  @HostListener('mouseenter')
  onMouseEnter() {
    this.hostHovered = true;
    [0,1].forEach((sIndex: number) => {
      if(this.scrollModes[sIndex] == ScrollMode.Hover && !this.isMouseState(this.scrollbarStates[sIndex]))
        this.updateHoverScrolling(sIndex);
    });
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    this.hostHovered = false;
    [0,1].forEach((sIndex: number) => {
      if(this.scrollModes[sIndex] == ScrollMode.Hover && !this.isMouseState(this.scrollbarStates[sIndex]))
        this.updateHoverScrolling(sIndex);
    });
  }

  handleWindowMouseMove: (event: MouseEvent) => void = (event: MouseEvent) => {
    if(this.pointerId == null)
      this.onPointerMove(event.clientX, event.clientY);
  }

  handleWindowMouseUp: () => void = () => {
    if(this.pointerId == null)
      this.onPointerUp();
  }

  handleWindowTouchMove: (event: TouchEvent) => void = (event: TouchEvent) => {
    if(this.pointerId == null)
      return;
    const touch: Touch = this.findTouch(event.changedTouches, (touch: Touch): boolean => touch.identifier == this.pointerId);
    if(touch) {
      event.preventDefault();
      this.onPointerMove(touch.clientX, touch.clientY);
    }
  }

  handleWindowTouchCancelEnd: (event: TouchEvent) => void = (event: TouchEvent) => {
    if(this.pointerId == null)
      return;
    const touch: Touch = this.findTouch(event.changedTouches, (touch: Touch): boolean => touch.identifier == this.pointerId);
    if(touch) {
      event.preventDefault();
      this.pointerId = null;
      this.onPointerUp();
    }
  }
}

export interface ScrollableConfig {
  x?: string,
  y?: string
}